import IconShipment from "@/components/icons/IconShipment";
import { FBAFreightClasses } from "@boxem/core-api-schema";
import { estimateFreightClass, FreightClasses } from "../../utils";
import { useForm, FormField } from "@/components/Form";
import StandardModal from "@/components/StandardModal";
import TextInput from "@/components/forms/TextInput/TextInput";
import InputNumber from "@/components/forms/InputNumber/InputNumber";
import DatePicker from "@/components/forms/DatePicker";
import Select from "@/components/forms/Select";
import { useEffect, useState } from "react";
import { FbaShipment } from "@/contexts/ShipmentContext";
import Tooltip from "@/components/Tooltip";
import IconInfo from "@/components/icons/IconInfo";

import styles from "./ShipperInformationModal.module.css";

export type ShipperInformation = {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  freightClass: keyof FreightClasses | null;
  declaredValue: number;
  freightReadyDate: Date;
};

const defaultValue: ShipperInformation = {
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  freightClass: null,
  declaredValue: 0,
  freightReadyDate: new Date,
};

interface Props {
  open: boolean;
  onClose: (value?: ShipperInformation, applyToAll?: boolean) => Promise<void>;
  shipmentsCount: number;
  value: ShipperInformation | null;
  shipment?: FbaShipment;
}

export default function ShipperInformationModal(props: Props) {
  const { open, shipmentsCount, onClose, value: initialValue, shipment } = props;
  const [estimatedFreightClass, setEstimatedFreightClass] = useState<string>();

  const form = useForm<ShipperInformation>(initialValue ?? defaultValue);
  useEffect(() => {
    if (open) form.setValue(initialValue ?? defaultValue)
  }, [open]);
  
  useEffect(() => {
    if (shipment) {
      const { value: freightClass } = estimateFreightClass(shipment);
      setEstimatedFreightClass(freightClass === "NONE" ? null : freightClass);
    }
  }, [shipment?.pallets]);

  const submit = async (applyToAll?: boolean) => {
    if (form.validate()) {
      await onClose(form.value as ShipperInformation, applyToAll);
    }
  };

  return (
    <StandardModal
      icon={IconShipment}
      title="Shipper Information"
      body={
        <div>
          <FormField
            form={form}
            name="contactName"
            displayName="Contact Name"
            showLabel={true}
          >
            <TextInput
              placeholder="Enter contact name"
              value={form.value.contactName ?? ""}
              onChange={value => form.setFieldValue("contactName", value)}
              invalid={form.state.contactName.invalid}
            />
          </FormField>
          <FormField
            form={form}
            name="contactEmail"
            displayName="Contact Email"
            showLabel={true}
            validator={(value?: string) => !value?.match(/^[^@]+@[^@]+?\.[^@]+$/) ? "Email address is invalid" : null}
          >
            <TextInput
              placeholder="Enter contact email"
              value={form.value.contactEmail ?? ""}
              onChange={value => form.setFieldValue("contactEmail", value)}
              invalid={form.state.contactEmail.invalid}
            />
          </FormField>
          <FormField
            form={form}
            name="contactPhone"
            displayName="Contact Phone"
            showLabel={true}
            validator={(value?: string) => !value?.match(/^(\+1)?[()\-. ]*(\d[()\-. ]*){10}$/) ? "Phone number is invalid" : null}
          >
            <TextInput
              placeholder="Enter contact phone"
              value={form.value.contactPhone ?? ""}
              onChange={value => form.setFieldValue("contactPhone", value)}
              invalid={form.state.contactPhone.invalid}
            />
          </FormField>
          <FormField
            form={form}
            name="freightClass"
            displayName="Freight Class"
            showLabel={true}
          >
            <Select<keyof FreightClasses>
              fullWidth={true}
              placeholder="Select freight class"
              value={form.value.freightClass ?? null}
              onChange={value => form.setFieldValue("freightClass", value)}
              options={FBAFreightClasses}
              optionRender={(option, index) => {
                return <div className={styles.freightClassOption}>
                  {FreightClasses[option].description}
                  {option === estimatedFreightClass && (
                    <Tooltip title="This is an estimate based on the density of the pallets in your shipment.">
                      <span className={`strong ${styles.freightClassOption}`}>-<IconInfo/>Boxem estimate</span>
                    </Tooltip>
                  )}
                </div>
              }}
              invalid={form.state.freightClass.invalid}
            />
          </FormField>
          <FormField
            form={form}
            name="declaredValue"
            displayName="Declared Value"
            showLabel={true}
            validator={(value?: number) => value <= 0 ? "Declared must be greater than zero" : null}
          >
            <InputNumber
              prefix={<span className="weak">$</span>}
              placeholder="0.00"
              value={form.value.declaredValue ?? 0}
              onChange={value => form.setFieldValue("declaredValue", value)}
              showControls={false}
              centerText={false}
              invalid={form.state.declaredValue.invalid}
            />
          </FormField>
          <FormField
            form={form}
            name="freightReadyDate"
            displayName="Freight Ready Date"
            showLabel={true}
          >
            <DatePicker
              placeholder="Enter freight ready date"
              value={form.value.freightReadyDate ?? null}
              onChange={value => form.setFieldValue("freightReadyDate", value)}
              invalid={form.state.freightReadyDate.invalid}
            />
          </FormField>
        </div>
      }
      open={open}
      onClose={onClose}
      submitText="Set For This Shipment"
      onSubmit={submit}
      altSubmitText={shipmentsCount > 1 ? `Set For All Shipments (${shipmentsCount})` : undefined}
      onAltSubmit={shipmentsCount > 1 ? () => submit(true) : undefined}
    />
  )
}
